import { API_URL } from "../../config/connect";

const getAuthHeaders = () => {
  const token = localStorage.getItem('google_id_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Get Stories Analytics - Active Hours
 * GET /api/v1/sales-manager/analytics/stories/active-hours
 * @param {Object} params - Query parameters
 * @param {number} params.days - Number of days to analyze (1-30, default: 7)
 * @returns {Promise<Object>} Active hours analytics data
 */
export const getStoriesActiveHours = async (params = {}) => {
  try {
    let url = `${API_URL}/api/v1/sales-manager/analytics/stories/active-hours`;
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days);
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching active hours analytics:', error);
    throw error;
  }
};

/**
 * Get Stories Analytics - Popular Searches
 * GET /api/v1/sales-manager/analytics/stories/popular-searches
 * @param {Object} params - Query parameters
 * @param {number} params.days - Number of days to analyze (1-30, default: 7)
 * @param {number} params.limit - Number of results to return (1-100, default: 20)
 * @param {number} params.category_id - Filter by category ID
 * @returns {Promise<Object>} Popular searches analytics data
 */
export const getStoriesPopularSearches = async (params = {}) => {
  try {
    let url = `${API_URL}/api/v1/sales-manager/analytics/stories/popular-searches`;
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching popular searches analytics:', error);
    throw error;
  }
};

/**
 * Get Stories Analytics - Category Interest
 * GET /api/v1/sales-manager/analytics/stories/category-interest
 * @param {Object} params - Query parameters
 * @param {number} params.days - Number of days to analyze (1-30, default: 7)
 * @returns {Promise<Object>} Category interest analytics data
 */
export const getStoriesCategoryInterest = async (params = {}) => {
  try {
    let url = `${API_URL}/api/v1/sales-manager/analytics/stories/category-interest`;
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days);
    
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching category interest analytics:', error);
    throw error;
  }
};

/**
 * Log Search Query
 * POST /api/v1/search/log
 * @param {Object} searchData - Search data
 * @param {string} searchData.query - Search query (required)
 * @param {number} searchData.category_id - Category ID (optional)
 * @param {number} searchData.subcategory_id - Subcategory ID (optional)
 * @returns {Promise<Object>} Log confirmation
 */
export const logSearchQuery = async (searchData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/search/log`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(searchData)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error logging search query:', error);
    throw error;
  }
};

